import { getNextUuid, getSlsTypesByKey } from '@fs/zion-sls'
import { namePartTypeKeys } from '../../shared/utils/helpers'

/**
 * Template object for a name part structure.
 *
 * @typedef {Object} NamePartTemplate
 * @property {string} elementId - Unique identifier for the intermediate element (e.g., NAME_GIVEN). Used for REM_SUB actions.
 * @property {string} fieldId - Unique identifier for the field element. Used for ADD_FV actions.
 * @property {boolean} added - Indicates if the name part has been added (newly created).
 * @property {string} type - The type of the name part (e.g., 'NAME_GN', 'NAME_SURN', etc.).
 * @property {boolean} removed - Indicates if the name part has been removed. For new parts, this is false.
 */

/**
 * Default template object for a name structure.
 *
 * @typedef {Object} NameTemplate
 * @property {string} id - Unique identifier for the template. Generated using getNextUuid().
 * @property {boolean} added - Indicates if the template has been added (newly created).
 * @property {Object} type - The name type object containing key and primaryText.
 * @property {string[]} parts - Array of name parts keys included in the name.
 * @property {string[]} removedParts - Array of part keys that have been removed from the name.
 * @property {NamePartTemplate} [NAME_PRE] - Name prefix part object (optional).
 * @property {NamePartTemplate} [NAME_GN] - Given name part object (optional).
 * @property {NamePartTemplate} [NAME_PATR] - Patronymic part object (optional).
 * @property {NamePartTemplate} [NAME_MATR] - Matronymic part object (optional).
 * @property {NamePartTemplate} [NAME_SPRE] - Secondary prefix part object (optional).
 * @property {NamePartTemplate} [NAME_SURN] - Surname part object (optional).
 * @property {NamePartTemplate} [NAME_SUF] - Suffix part object (optional).
 */

/**
 * Function to generate a new name template with default values.
 *
 * @function getDefaultNameTemplate
 * @returns {NameTemplate} A new name template object with default values.
 */
export const getDefaultNameTemplate = (person) => {
  const createNamePart = (fieldType) => {
    const elementId = getNextUuid()
    return {
      elementId, // ID for the intermediate element (e.g., NAME_GIVEN)
      id: getNextUuid(), // ID for the field element
      added: true,
      type: fieldType,
      removed: false,
      value: '',
      dispatches: [], // Placeholder for any dispatches related to this name part (e.g., for highlights integration)
      shared: false, // Newly created fields are not shared by default
      superElements: [], // Store super elements for later reference (e.g., when determining if a field is shared)
      linkedPersonIds: person ? [{ id: person.id, partitionKey: person.partitionKey }] : [], // Store linked person IDs for this name part
    }
  }

  return {
    id: getNextUuid(),
    added: true,
    type: { key: 'BIRTH_NAME', primaryText: 'Birth Name' },
    parts: ['NAME_GN', 'NAME_SURN'],
    removedParts: [],
    NAME_GN: createNamePart('NAME_GN'),
    NAME_SURN: createNamePart('NAME_SURN'),
  }
}

/**
 * Sorts elements based on their position in the `namePartTypeKeys` array.
 *
 * This is useful for ordering name parts in a consistent manner so they can be displayed in a logical sequence.
 * This is for using in array sort functions.
 *
 * @param {Object|string} a - The first element or object to compare.
 * @param {Object|string} b - The second element or object to compare.
 * @param {string} [key] - Optional key to extract the value from objects `a` and `b` for comparison.
 * @returns {number} A negative number if `a` should come before `b`, positive if after, or 0 if equal.
 */
export const sortNamePartTypes = (a, b, key) =>
  namePartTypeKeys.indexOf(key ? a[key] : a?.type || a) - namePartTypeKeys.indexOf(key ? b[key] : b?.type || b)

/**
 * Retrieves an SLS name type object from the provided list by matching the specified key.
 *
 * @param {Array} slsTypes - The array of SLS type objects to search within.
 * @param {string} key - The key to match against the `nameType` property.
 * @returns {Object|undefined} The matched SLS name type object, or undefined if not found.
 */
export const getSlsNameTypeByKey = (slsTypes, key) => {
  const slsNameTypes = getSlsTypesByKey(slsTypes, 'names')
  return slsNameTypes.find((type) => type.nameType === key)
}
